<template>
  <cv-content id="main-content" class="app-content" :class="cssClass">
    <div class="content-wrapper">
      <slot />
    </div>
  </cv-content>
</template>

<script setup lang="ts">
import { isEmpty } from "@/utils/var.check";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import UserActivityTrack from "@plugins/user-activity-track/index.ts";
import { PROVIDER_TYPE } from "@plugins/user-activity-track/service/user.activity.track.constant.ts";
import userActivityTrackEvent from "@plugins/user-activity-track/service/user.activity.track.event.ts";
import { appLocalStorage } from "@utils/app.storage";
import { computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useErrorBanner } from "@modules/app/v2/composables/error-banner.ts";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant.ts";

// Props declaration
interface Props {
  fullWidth?: boolean;
}

const { fullWidth = false } = defineProps<Props>();

// i18n
const { t } = useI18n();

// store
const store = useStore();
const toast = useToast();

useErrorBanner();

// computed
const checkNetworkConnected = computed<boolean>(() => store.getters["app/checkNetworkConnected"]);
const userInfoFormatted = computed(() => store.getters["auth/userInfoFormatted"]);
const isAdmin = computed<boolean>(() => store.getters["auth/isAdmin"]);
const isStudent = computed<boolean>(() => store.getters["auth/isStudent"]);
const isTeacher = computed<boolean>(() => store.getters["auth/isTeacher"]);
const isEAssessment = computed(() => store.getters[`${NAMESPACE.ATTEMPT}/isEAssessment`]);

const cssClass = computed(() => {
  return {
    "_full-width": fullWidth,
  };
});

// methods
function initializeUserActivityTrack(): void {
  const userInfoFormattedValue = userInfoFormatted.value;
  if (userInfoFormattedValue && Object.keys(userInfoFormattedValue).length > 0) {
    let role = "none";
    if (isAdmin.value) {
      role = "admin";
    } else if (isTeacher.value) {
      role = "teacher";
    } else if (isStudent.value) {
      role = "student";
    }

    const userInfo = {
      ...userInfoFormattedValue,
      role: role,
      userAgent: {
        raw: navigator.userAgent,
      },
    };

    UserActivityTrack.initialize(PROVIDER_TYPE, userInfo, null).then(() => {
      const redirectedFrom = appLocalStorage.getItem("redirectedFrom");
      if (!isEmpty(redirectedFrom)) {
        userActivityTrackEvent.redirectPage(JSON.parse(redirectedFrom));
        appLocalStorage.removeItem("redirectedFrom");
      }
    });
  }
}

// watchers
watch(checkNetworkConnected, (newValue) => {
  const isAttemptAssessment = isEAssessment.value && isStudent.value;
  if (isAttemptAssessment) {
    return;
  }

  if (!newValue) {
    console.error(
      "Network disconnected - toast service needs to be implemented for Composition API",
    );
    toast.show("error", {
      title: t("app_offline_title"),
      subTitle: t("app_offline_message"),
      hideCloseButton: true,
      hasTimeout: false,
    });
  } else {
    toast.removeAll();
  }
});

// lifecycle hooks
onMounted(() => {
  initializeUserActivityTrack();
});
</script>
